import type {
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from '@reduxjs/toolkit/query/react';
import { sumBy } from 'lodash';

import type {
  PostAddAdultsToSubUnitRequest,
  PostAddDenChiefRequest,
  PostAddSubUnitRequest,
  PostAddSubUnitResponse,
  PostAddYouthsToSubUnitRequest,
  PostRemoveDenChiefRequest,
  PutSubUnitRequest,
  SubUnitType,
} from '@appTypes/esb';
import type { QueryFnBaseQuery, QueryFnResult } from '@modules/rtk/utils';

/**
 * Steps of the combined sub-unit mutations. `failed` is for the user, not
 * for replaying: a retry rebuilds its arg from the refetched server state.
 */
export type SubUnitSaveStep =
  | 'rename'
  | 'youths'
  | 'adults'
  | 'denChiefs'
  | 'remove';

/** Arg of `createSubUnitWithMembers`: create the sub-unit, then assign people. */
export interface CreateSubUnitWithMembersArg {
  orgGuid: string;
  subUnitType: SubUnitType;
  body: PostAddSubUnitRequest;
  youths: PostAddYouthsToSubUnitRequest;
  adults: PostAddAdultsToSubUnitRequest;
  /** Dens only; one `denChief/add` call each, `denId` set to the new id */
  denChiefs: PostAddDenChiefRequest[];
}

/** Arg of `updateSubUnitWithMembers`: only the changes against the server. */
export interface UpdateSubUnitWithMembersArg {
  orgGuid: string;
  subUnitType: SubUnitType;
  subUnitId: number;
  /** Only when the name or den level changed */
  rename?: PutSubUnitRequest;
  /** New youths, plus the existing ones when the den level changed */
  youths: PostAddYouthsToSubUnitRequest;
  adults: PostAddAdultsToSubUnitRequest;
  /** One `denChief/add` call each, `denId` set to the sub-unit */
  addDenChiefs: PostAddDenChiefRequest[];
  /** `sbUserId`s of removed youths and adults (`DELETE …/users`) */
  removeUserIds: number[];
  /** Removed den chiefs; one `denChief/remove` call each, `denId` set too */
  removeDenChiefs: PostRemoveDenChiefRequest[];
}

/** Arg of `deleteSubUnitWithMembers`: empty the sub-unit, then delete it. */
export interface DeleteSubUnitWithMembersArg {
  orgGuid: string;
  subUnitType: SubUnitType;
  subUnitId: number;
  /** Current youths and adults; empty on a retry once they're removed */
  memberUserIds: number[];
  /** Current den chiefs; empty on a retry once they're removed */
  denChiefs: PostRemoveDenChiefRequest[];
}

/** Result of the create and update mutations, including partial success. */
export interface SubUnitSaveResult {
  subUnitId: number;
  failed: SubUnitSaveStep[];
}

const subUnitUrl = (orgGuid: string, subUnitType: string, subUnitId: number) =>
  `/organizations/v2/units/${orgGuid}/${subUnitType}/${subUnitId}`;

/** Removes youths and adults, not den chiefs (see `denChiefCall`) */
const deleteUsersCall = (
  orgGuid: string,
  subUnitType: string,
  subUnitId: number,
  userIds: number[],
): FetchArgs => ({
  url: `${subUnitUrl(
    orgGuid,
    subUnitType,
    subUnitId,
  )}/users?userId=${userIds.toString()}`,
  method: 'DELETE',
});

/** `denChief/add` or `remove` call for the sub-unit; sets its `denId` */
const denChiefCall = (
  action: 'add' | 'remove',
  orgGuid: string,
  subUnitId: number,
  body: PostAddDenChiefRequest | PostRemoveDenChiefRequest,
): FetchArgs => ({
  url: `/organizations/v2/${orgGuid}/denChief/${action}`,
  method: 'POST',
  body: { ...body, denId: [subUnitId] },
});

/** A failed call, with its `meta` for the Sentry logger */
interface FailedCall {
  error: FetchBaseQueryError;
  meta?: FetchBaseQueryMeta;
}

interface SaveStepRequests {
  step: SubUnitSaveStep;
  requests: FetchArgs[];
}

interface SaveStepOutcome {
  step: SubUnitSaveStep;
  /** Number of calls sent */
  sent: number;
  /** The calls that failed, in request order */
  failures: FailedCall[];
}

/**
 * Sends the requests of every step in parallel. Steps without requests are
 * skipped and left out of the outcome.
 */
const runSaveSteps = (
  baseQuery: QueryFnBaseQuery,
  steps: SaveStepRequests[],
): Promise<SaveStepOutcome[]> =>
  Promise.all(
    steps
      .filter(({ requests }) => requests.length > 0)
      .map(async ({ step, requests }) => {
        const results = await Promise.all(
          requests.map(request => baseQuery(request)),
        );

        return {
          step,
          sent: requests.length,
          failures: results.flatMap(({ error, meta }) =>
            error ? [{ error, meta }] : [],
          ),
        };
      }),
  );

const failedSteps = (outcomes: SaveStepOutcome[]): SubUnitSaveStep[] =>
  outcomes
    .filter(({ failures }) => failures.length > 0)
    .map(({ step }) => step);

/** One request for a non-empty list body, none for an empty one */
const unlessEmpty = <T>(body: T[], request: (body: T[]) => FetchArgs) =>
  body.length > 0 ? [request(body)] : [];

/** `queryFn` of `rosterApi.createSubUnitWithMembers` */
export async function runCreateSubUnitWithMembers(
  arg: CreateSubUnitWithMembersArg,
  baseQuery: QueryFnBaseQuery,
): Promise<QueryFnResult<SubUnitSaveResult>> {
  const { orgGuid, subUnitType } = arg;

  const created = await baseQuery({
    url: `/organizations/v2/units/${orgGuid}/${subUnitType}/add`,
    method: 'POST',
    body: arg.body,
  });
  if (created.error) return created;

  const subUnitId = (created.data as PostAddSubUnitResponse | null)?.id;
  if (typeof subUnitId !== 'number') {
    return {
      error: {
        status: 'CUSTOM_ERROR',
        error: 'The create response has no sub-unit id',
        data: created.data,
      },
      meta: created.meta,
    };
  }

  const url = subUnitUrl(orgGuid, subUnitType, subUnitId);
  const outcomes = await runSaveSteps(baseQuery, [
    {
      step: 'youths',
      requests: unlessEmpty(arg.youths, body => ({
        url: `${url}/youths`,
        method: 'POST',
        body,
      })),
    },
    {
      step: 'adults',
      requests: unlessEmpty(arg.adults, body => ({
        url: `${url}/adults`,
        method: 'POST',
        body,
      })),
    },
    {
      step: 'denChiefs',
      requests: arg.denChiefs.map(denChief =>
        denChiefCall('add', orgGuid, subUnitId, denChief),
      ),
    },
  ]);
  const failed = failedSteps(outcomes);
  const sent = sumBy(outcomes, 'sent');
  const failures = outcomes.flatMap(outcome => outcome.failures);

  // Nobody got in: an empty sub-unit never shows in the roster
  if (sent > 0 && failures.length === sent) {
    const rollback = await baseQuery({ url, method: 'DELETE' });
    // It still exists, so the modal must edit it rather than create again
    if (rollback.error) return { data: { subUnitId, failed } };

    return failures[0];
  }

  return { data: { subUnitId, failed } };
}

/** `queryFn` of `rosterApi.updateSubUnitWithMembers` */
export async function runUpdateSubUnitWithMembers(
  arg: UpdateSubUnitWithMembersArg,
  baseQuery: QueryFnBaseQuery,
): Promise<QueryFnResult<SubUnitSaveResult>> {
  const { orgGuid, subUnitType, subUnitId } = arg;
  const url = subUnitUrl(orgGuid, subUnitType, subUnitId);

  const outcomes = await runSaveSteps(baseQuery, [
    {
      step: 'rename',
      requests: arg.rename ? [{ url, method: 'PUT', body: arg.rename }] : [],
    },
    {
      step: 'youths',
      requests: unlessEmpty(arg.youths, body => ({
        url: `${url}/youths`,
        method: 'POST',
        body,
      })),
    },
    {
      step: 'adults',
      requests: unlessEmpty(arg.adults, body => ({
        url: `${url}/adults`,
        method: 'POST',
        body,
      })),
    },
    {
      step: 'denChiefs',
      requests: [
        ...arg.addDenChiefs.map(denChief =>
          denChiefCall('add', orgGuid, subUnitId, denChief),
        ),
        ...arg.removeDenChiefs.map(denChief =>
          denChiefCall('remove', orgGuid, subUnitId, denChief),
        ),
      ],
    },
    {
      step: 'remove',
      requests: unlessEmpty(arg.removeUserIds, userIds =>
        deleteUsersCall(orgGuid, subUnitType, subUnitId, userIds),
      ),
    },
  ]);

  return { data: { subUnitId, failed: failedSteps(outcomes) } };
}

/** `queryFn` of `rosterApi.deleteSubUnitWithMembers` */
export async function runDeleteSubUnitWithMembers(
  arg: DeleteSubUnitWithMembersArg,
  baseQuery: QueryFnBaseQuery,
): Promise<QueryFnResult<void>> {
  const { orgGuid, subUnitType, subUnitId } = arg;

  const removed = await runSaveSteps(baseQuery, [
    {
      step: 'remove',
      requests: unlessEmpty(arg.memberUserIds, userIds =>
        deleteUsersCall(orgGuid, subUnitType, subUnitId, userIds),
      ),
    },
    {
      step: 'denChiefs',
      requests: arg.denChiefs.map(denChief =>
        denChiefCall('remove', orgGuid, subUnitId, denChief),
      ),
    },
  ]);
  const [removeFailure] = removed.flatMap(outcome => outcome.failures);
  if (removeFailure) return removeFailure;

  const deleted = await baseQuery({
    url: subUnitUrl(orgGuid, subUnitType, subUnitId),
    method: 'DELETE',
  });
  if (deleted.error) return deleted;

  return { data: undefined };
}
